import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Template } from "../api/endpoints/fetchTemplates";
import { useAuth } from "../contexts/AuthContext";
import { fetchMembers, type UserDetailResponse } from "../api/endpoints/users";
import { buildSectionsFromDocx } from "../data/docToHtml";
import { useDocxFormTemplateDetails } from "../hooks/useDocxFormTemplateDetails";
import { useTemplateFormData } from "../hooks/useTemplateFormData";
import { SkeletonPreview } from "./SkeletonPreview";
import { DocToHtmlPreviewHeader } from "./doctohtml/DocToHtmlPreviewHeader";
import { DocToHtmlErrorBanner } from "./doctohtml/DocToHtmlErrorBanner";
import { useSaveDocxFormMutation } from "./doctohtml/useSaveDocxFormMutation";
import { useSubmitDocxFormMutation } from "./doctohtml/useSubmitDocxFormMutation";
import { usePublishFormMutation } from "./acroformpreview/usePublishFormMutation";
import { injectBadgeStyles } from "./acroformpreview/injectBadgeStyles";
import { addDocxRowClickListener } from "./doctohtml/addDocxRowHandler";
import { collectDocxFormValues, collectDocxFormValuesForSave, DocxRow, DocxTableInfo, type DocxFormData, type DocxFormSaveValues } from "./doctohtml/collectDocxFormValues";
import { restoreDocxFormValues } from "./doctohtml/restoreDocxFormValues";
import { initializeFormulaFields } from "../data/doctohtml/initializeFormulaFields";
import { SectionStepper } from "./SectionStepper";
import PreviewBanner from "./PreviewBanner";
import NavigationBar from "./NavigationBar";
import { X, Check } from "lucide-react";

interface DocToHtmlFormPreviewProps {
  template: Template;
  onClose: () => void;
  onSaved: (fileId: string) => void;
  onInfo: () => void;
  isInfoPanelOpen: boolean;
}

export function DocToHtmlFormPreview({
  template,
  onClose,
  onSaved,
  onInfo,
  isInfoPanelOpen,
}: DocToHtmlFormPreviewProps) {
  const displayTitle = template.fileTitle || template.fileName;
  const isPublishedForm = template.isPublished;
  const hasMemberId = !!template.memberId;
  const { user, isAdmin } = useAuth();

  // Fetch DOCX template details from API
  const { data: templateData, isLoading, isError, error } = useDocxFormTemplateDetails({
    templateId: template.fileId,
    userEmail: template.userEmail,
  });

  // Fetch saved form data
  const { data: formDataResponse, isSuccess: formDataSuccess, isLoading: isFormDataLoading } = useTemplateFormData({
    templateId: template?.fileId ?? null,
    fileName: template?.fileName ?? "",
  });

  // Parse saved form data
  const parsedFormData = useMemo(() => {
    try {
      const raw = formDataResponse?.data?.formData;
      if (!raw) return undefined;
      const parsed = JSON.parse(raw) as unknown;
      return {tableInfo: parsed as DocxTableInfo[]} as DocxFormSaveValues;
    } catch {
      return undefined;
    }
  }, [formDataResponse]);

  // Local state for save feedback
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const successTimerRef = useRef<number | null>(null);

  // Fetch member details if memberId exists
  const { data: members = [] } = useQuery<UserDetailResponse[]>({
    queryKey: ["memberlist"],
    queryFn: fetchMembers,
    enabled: !!template.memberId,
  });

  // Find member details by memberId
  const memberData = members.find((m) => m.id === template.memberId);
  const memberName = memberData
    ? `${memberData.firstName || ""} ${memberData.lastName || ""}`.trim()
    : "";
  const displayTitleWithMember = memberName
    ? `${displayTitle} - ${memberName}`
    : displayTitle;

  // Only disable save/submit for non-admin users when memberId doesn't exist
  const isSaveDisabled = !isAdmin && !hasMemberId;

  // Track Save Changes intent
  const saveClickedRef = useRef(false);

  // Build sections from DOCX schema
  const { headersHtml, sections } = React.useMemo(() => {
    if (!templateData?.json) return { headersHtml: "", sections: [] };
    try {
      return buildSectionsFromDocx(templateData.json);
    } catch (err) {
      console.error("Failed to build sections from DOCX JSON:", err);
      return { headersHtml: "", sections: [] };
    }
  }, [templateData?.json]);

  // Section navigation state
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const sectionCount = sections.length;
  const isFirst = currentSectionIndex === 0;
  const isLast = currentSectionIndex === sectionCount - 1;
  const currentLabel = `Section ${currentSectionIndex + 1} of ${sectionCount}`;

  // Track modified HTML for each section (to preserve dynamically added rows)
  const [sectionHtmlCache, setSectionHtmlCache] = useState<Record<number, string>>({});

  // Track form values for each section in DOCX format (grouped by table)
  const [sectionValuesCache, setSectionValuesCache] = useState<Record<number, DocxFormSaveValues>>({});

  // Track if initial values have been applied
  const valuesAppliedRef = useRef(false);

  // Calculate field count
  const fieldCount = React.useMemo(() => {
    const container = document.createElement("div");
    return sections.reduce((acc, s) => {
      container.innerHTML = s.html;
      const inputs = container.querySelectorAll("input, textarea, select");
      return acc + inputs.length;
    }, 0);
  }, [sections]);

  // Capture current section's HTML and values (including dynamically added rows)
  const captureCurrentSectionHtml = useCallback(() => {
    const container = document.querySelector(".preview-form-content");
    if (container) {
      // Capture HTML structure
      setSectionHtmlCache((prev) => ({
        ...prev,
        [currentSectionIndex]: container.innerHTML,
      }));

      // Capture form values in DOCX format grouped by table
      const formData = collectDocxFormValuesForSave(".preview-form-content");
      setSectionValuesCache((prev) => ({
        ...prev,
        [currentSectionIndex]: formData,
      }));
    }
  }, [currentSectionIndex]);

  // Handle section selection
  const handleSelectSection = (idx: number) => {
    captureCurrentSectionHtml();
    setCurrentSectionIndex(idx);
  };

  // Handle previous/next
  const handlePrevious = () => {
    if (!isFirst) {
      captureCurrentSectionHtml();
      setCurrentSectionIndex(currentSectionIndex - 1);
    }
  };

  const handleNext = () => {
    if (!isLast) {
      captureCurrentSectionHtml();
      setCurrentSectionIndex(currentSectionIndex + 1);
    }
  };

  // Inject minimal CSS for success/error badges
  useEffect(() => {
    injectBadgeStyles();
  }, []);

  // Add row click listener for "Click to Add Row" buttons
  useEffect(() => {
    const cleanup = addDocxRowClickListener();
    return cleanup;
  }, [currentSectionIndex]);

  // Restore cached values after section HTML is rendered
  useEffect(() => {
    const cachedValues = sectionValuesCache[currentSectionIndex];
    if (cachedValues && cachedValues.tableInfo && cachedValues.tableInfo.length > 0) {
      // Use cached values if available (user has visited this section before)
      // Use requestAnimationFrame to ensure DOM is ready after dangerouslySetInnerHTML
      requestAnimationFrame(() => {
        setTimeout(() => {
          restoreDocxFormValues(cachedValues.tableInfo, ".preview-form-content");
          // Initialize formula fields after restoring values
          initializeFormulaFields(".preview-form-content");
        }, 50);
      });
    } else if (parsedFormData && parsedFormData.tableInfo && parsedFormData.tableInfo.length > 0 && formDataSuccess) {
      // If no cached values but we have parsed form data, try to restore from it
      requestAnimationFrame(() => {
        setTimeout(() => {
          restoreDocxFormValues(parsedFormData.tableInfo, ".preview-form-content");
          // Initialize formula fields after restoring values
          initializeFormulaFields(".preview-form-content");
        }, 50);
      });
    } else {
      // No cached or saved data - just initialize formula fields for empty form
      requestAnimationFrame(() => {
        setTimeout(() => {
          initializeFormulaFields(".preview-form-content");
        }, 50);
      });
    }
  }, [currentSectionIndex, sectionValuesCache, parsedFormData, formDataSuccess]);

  // Apply saved form data on initial load (similar to XFA implementation)
  useEffect(() => {
    if (!formDataSuccess) return;
    if (valuesAppliedRef.current) return;
    const container = document.querySelector(".preview-form-content");
    if (!container) return;

    valuesAppliedRef.current = true;

    setTimeout(() => {
      console.log("Applying saved DOCX form data:", parsedFormData);
      if (parsedFormData && parsedFormData.tableInfo && parsedFormData.tableInfo.length !== 0) {
        // Flatten table-grouped data to rows for restoration
        restoreDocxFormValues(parsedFormData.tableInfo, ".preview-form-content");
        
        // Cache the parsed data directly (it's already in DocxFormSaveValues format)
        setSectionValuesCache((prev) => ({
          ...prev,
          [currentSectionIndex]: parsedFormData,
        }));
      }
      
      // Initialize formula fields after applying saved data
      initializeFormulaFields(".preview-form-content");
    }, 300);
  }, [formDataSuccess, templateData?.json, parsedFormData, currentSectionIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Convert user to MinimalUser format
  type MinimalUser = { email?: string; role?: "admin" | "member" } | null;
  const minimalUser: MinimalUser = user
    ? {
      email: user.email,
      role: user.role === "manager" ? "admin" : user.role,
    }
    : null;

  // Save form mutation
  const { mutate, isPending: mutationIsPending } = useSaveDocxFormMutation({
    template,
    user: minimalUser,
    successTimerRef,
    setShowSuccess,
    setErrorText,
    onSaved,
    onClose,
  });

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  // Submit form mutation
  const submitFormMutation = useSubmitDocxFormMutation({
    template,
    user: minimalUser,
    email: user?.email || "",
    successTimerRef,
    setShowSuccess,
    setErrorText,
    onSaved,
    onClose,
  });

  // Publish form mutation
  const publishFormMutation = usePublishFormMutation({
    template,
    email: user?.email || "",
    successTimerRef,
    setShowSuccess,
    setErrorText,
    onSaved,
    onClose,
  });

  // Collect all form values from all sections in DOCX format for submit
  const collectAllFormValues = useCallback(() => {
    // Collect current section values directly from DOM (latest values)
    const currentSectionData = collectDocxFormValues(".preview-form-content");
    
    // Combine all sections' data
    const allRows: DocxFormData['rows'] = [];

    // Add values from current section first (these are the latest)
    if (currentSectionData.rows) {
      allRows.push(...currentSectionData.rows);
    }

    // Iterate through other sections and add their cached values (convert from table format)
    sections.forEach((_, index) => {
      // Skip current section as we already added it
      if (index === currentSectionIndex) return;
      
      const cachedData = sectionValuesCache[index];
      if (cachedData && cachedData.tableInfo) {
        // Flatten table-grouped data to row format
        const flatRows = cachedData.tableInfo.flatMap(table => table.rows);
        allRows.push(...flatRows);
      }
    });

    return { rows: allRows };
  }, [sections, sectionValuesCache, currentSectionIndex]);

  // Enable submit only on last section
  const isSubmitEnabled = sectionCount === 1 || isLast;

  // Handle Save Changes - collect from all sections
  const handleSaveChanges = useCallback(() => {
    saveClickedRef.current = true;
    
    // Collect current section values
    const currentSectionData = collectDocxFormValuesForSave(".preview-form-content");
    
    // Start with existing data from API (source of truth)
    const allTableInfo = [...(parsedFormData?.tableInfo || [])];
    
    // Create a map of tableIds from current and cached data to track which tables we're updating
    const updatedTableIds = new Set<string>();
    
    // Update with current section data
    currentSectionData.tableInfo?.forEach(table => {
      updatedTableIds.add(table.tableId);
      const existingIndex = allTableInfo.findIndex(t => t.tableId === table.tableId);
      if (existingIndex >= 0) {
        allTableInfo[existingIndex] = table;
      } else {
        allTableInfo.push(table);
      }
    });
    
    // Update with cached data from other visited sections
    sections.forEach((_, index) => {
      if (index === currentSectionIndex) return;
      
      const cachedData = sectionValuesCache[index];
      if (cachedData && cachedData.tableInfo) {
        cachedData.tableInfo.forEach(table => {
          updatedTableIds.add(table.tableId);
          const existingIndex = allTableInfo.findIndex(t => t.tableId === table.tableId);
          if (existingIndex >= 0) {
            allTableInfo[existingIndex] = table;
          } else {
            allTableInfo.push(table);
          }
        });
      }
    });
    
    const formData: DocxFormSaveValues = { tableInfo: allTableInfo };
    mutate({ payload: formData });
  }, [sections, sectionValuesCache, currentSectionIndex, mutate, parsedFormData]);

  // Handle Submit Form
  const handleSubmitForm = useCallback(() => {
    // First, save the form data in table-grouped format
    const currentSectionData = collectDocxFormValuesForSave(".preview-form-content");
    
    // Start with existing data from API (source of truth)
    const allTableInfo = [...(parsedFormData?.tableInfo || [])];
    
    // Create a map of tableIds to track which tables we're updating
    const updatedTableIds = new Set<string>();
    
    // Update with current section data
    currentSectionData.tableInfo?.forEach(table => {
      updatedTableIds.add(table.tableId);
      const existingIndex = allTableInfo.findIndex(t => t.tableId === table.tableId);
      if (existingIndex >= 0) {
        allTableInfo[existingIndex] = table;
      } else {
        allTableInfo.push(table);
      }
    });
    
    // Update with cached data from other visited sections
    sections.forEach((_, index) => {
      if (index === currentSectionIndex) return;
      const cachedData = sectionValuesCache[index];
      if (cachedData && cachedData.tableInfo) {
        cachedData.tableInfo.forEach(table => {
          updatedTableIds.add(table.tableId);
          const existingIndex = allTableInfo.findIndex(t => t.tableId === table.tableId);
          if (existingIndex >= 0) {
            allTableInfo[existingIndex] = table;
          } else {
            allTableInfo.push(table);
          }
        });
      }
    });
    
    const saveFormData: DocxFormSaveValues = { tableInfo: allTableInfo };
    mutate({ payload: saveFormData });
    
    // Then, submit the form data in flat format (flatten all table data)
    const flatRows = allTableInfo.flatMap(table => table.rows);
    const submitFormData: DocxFormData = { rows: flatRows };
    submitFormMutation.mutate({ payload: submitFormData });
  }, [submitFormMutation, sections, sectionValuesCache, currentSectionIndex, mutate, parsedFormData]);

  // Handle Publish Form
  const handlePublishForm = useCallback(() => {
    publishFormMutation.mutate();
  }, [publishFormMutation]);

  if (isLoading) return <SkeletonPreview />;

  if (isError) {
    return (
      <DocToHtmlErrorBanner
        message={error instanceof Error ? error.message : undefined}
      />
    );
  }

  return (
    <div
      className={`preview-form fixed inset-0 bg-white z-50 flex flex-col transition-all duration-300 ${isInfoPanelOpen ? "backdrop-blur-sm bg-white/95" : ""
        }`}
    >
      {/* Preview Header */}
      <DocToHtmlPreviewHeader
        displayTitle={displayTitleWithMember}
        onInfo={onInfo}
        onClose={onClose}
        isInfoPanelOpen={isInfoPanelOpen}
      />

      <div
        className={`flex flex-col flex-auto overflow-hidden transition-all duration-300 ${isInfoPanelOpen ? "opacity-75" : ""
          }`}
      >
        {/* Section Stepper */}
        {sections.length > 1 && (
          <SectionStepper
            sections={sections.map((s) => ({ id: s.id, name: s.name }))}
            currentIndex={currentSectionIndex}
            onSelect={handleSelectSection}
          />
        )}

        {/* Action Bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-3">
          <div className="flex items-center justify-end gap-3">
            {errorText && (
              <span className="error-badge" aria-live="assertive">
                <span className="error-badge__icon" aria-hidden="true">
                  <X className="w-3 h-3" />
                </span>
                <span className="text-sm text-red-600 truncate max-w-[200px]">
                  {errorText}
                </span>
              </span>
            )}
            {showSuccess && (
              <span className="success-badge" aria-live="polite">
                <span className="success-badge__icon" aria-hidden="true">
                  <Check className="w-3 h-3" />
                </span>
                <span className="text-sm text-green-600">Saved</span>
              </span>
            )}
            {isPublishedForm && (
              <>
                <button
                  type="button"
                  onClick={handleSaveChanges}
                  disabled={isSaveDisabled || mutationIsPending || submitFormMutation.isPending || publishFormMutation.isPending}
                  className={`px-4 py-1.5 text-sm rounded-full transition ${isSaveDisabled || mutationIsPending || submitFormMutation.isPending || publishFormMutation.isPending
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "btn-secondary"
                    }`}
                >
                  {mutationIsPending ? "Saving..." : "Save Changes"}
                </button>
                <button
                  type="button"
                  onClick={handleSubmitForm}
                  disabled={isSaveDisabled || !isSubmitEnabled || mutationIsPending || submitFormMutation.isPending || publishFormMutation.isPending}
                  className={`px-4 py-1.5 text-sm rounded-full transition ${isSaveDisabled || !isSubmitEnabled || mutationIsPending || submitFormMutation.isPending || publishFormMutation.isPending
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "btn-primary"
                    }`}
                >
                  {submitFormMutation.isPending ? "Submitting..." : "Submit Form"}
                </button>
              </>
            )}
            {!isPublishedForm && (
              <button
                type="button"
                onClick={handlePublishForm}
                disabled={mutationIsPending || submitFormMutation.isPending || publishFormMutation.isPending}
                className={`px-4 py-1.5 text-sm rounded-full transition ${mutationIsPending || submitFormMutation.isPending || publishFormMutation.isPending
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "btn-primary"
                  }`}
              >
                {publishFormMutation.isPending ? "Publishing..." : "Publish Form"}
              </button>
            )}
          </div>
        </div>

        {/* Form Content */}
        <div className="flex-auto overflow-y-auto px-6 py-6">
          <div className="max-w-6xl mx-auto bg-white border border-gray-200 rounded-lg p-4">
            {/* Headers */}
            {headersHtml && !isFormDataLoading && (
              <div className="mb-4" dangerouslySetInnerHTML={{ __html: headersHtml }} />
            )}
            
            {/* Loading indicator for form data */}
            {isFormDataLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-blue-600 animate-spin" />
                  <p className="text-sm text-gray-600">Loading form data...</p>
                </div>
              </div>
            ) : (
              /* Current Section */
              <div
                className="preview-form-content preview-doc-form prose max-w-none flex flex-col gap-4"
                dangerouslySetInnerHTML={{
                  __html: sectionHtmlCache[currentSectionIndex] || sections[currentSectionIndex]?.html || ""
                }}
              />
            )}
          </div>
        </div>

        {/* Navigation Bar */}
        <NavigationBar
          isFirst={isFirst}
          isLast={isLast}
          currentLabel={currentLabel}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />

        {/* Preview Banner */}
        <PreviewBanner
          sectionCount={sectionCount}
          subsectionCount={0}
          fieldCount={fieldCount}
        />
      </div>
    </div>
  );
}

export default DocToHtmlFormPreview;
